import type {
  GenerateTaskTypeStageTreeInput,
  TaskTypeResponse,
  TaskTypeStageTreeNode,
} from '@erp/rpc';
import {
  TaskIntendedAssigneeSchema,
  type TaskExpectedSubtask,
  type TaskIntendedAssignee,
} from '@erp/shared-types';
import { cn } from 'src/lib/utils';
import {
  editorStateFor,
  isValidTaskTypeKey,
  type TaskTypeEditorState,
} from './taskTypeAdmin';

export type TaskTypeStageOption = {
  id: string;
  key: string;
  name: string;
  expectedSubtasks: TaskExpectedSubtask[];
};

export type StageTreeDraftNode = {
  clientId: string;
  key: string;
  name: string;
  description: string;
  intendedAssignee: TaskIntendedAssignee | undefined;
  children: StageTreeDraftNode[];
};

let draftNodeId = 0;

export function parseIntendedAssignee(
  value: string,
): TaskIntendedAssignee | undefined {
  const parsed = TaskIntendedAssigneeSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

/** Shared tree-card chrome so persisted and AI-draft checklists match. */
export function stageNodeCardClassName(nested: boolean) {
  return cn(
    'rounded-md border',
    nested
      ? 'border-border/70 border-l-2 border-l-muted-foreground/50 bg-muted/50'
      : 'border-border bg-background',
  );
}

export function buildGenerateStageTreeInput(opts: {
  prompt: string;
  parent: { key: string; name: string; description: string | null };
  stageOptions: readonly TaskTypeStageOption[];
  expectedSubtasks: readonly TaskExpectedSubtask[];
}): GenerateTaskTypeStageTreeInput {
  const existingTypes: { key: string; name: string }[] = [];
  for (const option of opts.stageOptions) {
    if (option.key === opts.parent.key) continue;
    existingTypes.push({ key: option.key, name: option.name });
    if (existingTypes.length >= 200) break;
  }
  return {
    prompt: opts.prompt,
    parent: opts.parent,
    existingTypes,
    currentStages: buildCurrentStageTree(
      opts.expectedSubtasks,
      opts.stageOptions,
    ),
  };
}

export function newStageDraftNode(
  partial?: Partial<Omit<StageTreeDraftNode, 'clientId'>>,
): StageTreeDraftNode {
  draftNodeId += 1;
  return {
    clientId: `stage-draft-${draftNodeId}`,
    key: '',
    name: '',
    description: '',
    intendedAssignee: undefined,
    children: [],
    ...partial,
  };
}

export function stageTreeToDraft(
  stages: readonly TaskTypeStageTreeNode[],
): StageTreeDraftNode[] {
  return stages.map((node) =>
    newStageDraftNode({
      key: node.key,
      name: node.name,
      description: node.description ?? '',
      intendedAssignee: node.intendedAssignee ?? undefined,
      children: stageTreeToDraft(node.stages),
    }),
  );
}

export function draftToStageTree(
  nodes: readonly StageTreeDraftNode[],
): TaskTypeStageTreeNode[] {
  return nodes.map((node) => ({
    key: node.key.trim(),
    name: node.name.trim(),
    description: node.description.trim() ? node.description.trim() : null,
    intendedAssignee: node.intendedAssignee ?? null,
    stages: draftToStageTree(node.children),
  }));
}

export function buildCurrentStageTree(
  entries: readonly TaskExpectedSubtask[],
  stageOptions: readonly TaskTypeStageOption[],
  visited: ReadonlySet<string> = new Set(),
): TaskTypeStageTreeNode[] {
  return entries.map((entry) => {
    const option = stageOptions.find(
      (candidate) => candidate.key === entry.key,
    );
    const alreadyVisited = visited.has(entry.key);
    const nextVisited = new Set(visited);
    nextVisited.add(entry.key);
    return {
      key: entry.key,
      name: option?.name ?? entry.key,
      description: null,
      intendedAssignee: entry.intendedAssignee ?? null,
      stages:
        option && !alreadyVisited
          ? buildCurrentStageTree(
              option.expectedSubtasks,
              stageOptions,
              nextVisited,
            )
          : [],
    };
  });
}

export function nestedStagesFor(
  key: string,
  stageOptions: readonly TaskTypeStageOption[],
  visited: ReadonlySet<string>,
): TaskExpectedSubtask[] {
  if (visited.has(key)) return [];
  const option = stageOptions.find((candidate) => candidate.key === key);
  return option?.expectedSubtasks ?? [];
}

export type StageDraftIssue = {
  clientId: string;
  message: 'key_invalid' | 'name_required' | 'key_duplicate' | 'parent_key';
};

export function stageDraftKeyAriaInvalid(
  message: StageDraftIssue['message'] | undefined,
): boolean {
  return (
    message === 'key_invalid' ||
    message === 'key_duplicate' ||
    message === 'parent_key'
  );
}

export function validateStageDraft({
  nodes,
  parentKey,
}: {
  nodes: readonly StageTreeDraftNode[];
  parentKey: string;
}): StageDraftIssue[] {
  const issues: StageDraftIssue[] = [];
  const seen = new Set<string>();

  function visit(list: readonly StageTreeDraftNode[]) {
    for (const node of list) {
      if (!node.name.trim()) {
        issues.push({ clientId: node.clientId, message: 'name_required' });
      }
      const key = node.key.trim();
      if (!isValidTaskTypeKey(key)) {
        issues.push({ clientId: node.clientId, message: 'key_invalid' });
      } else if (key === parentKey) {
        issues.push({ clientId: node.clientId, message: 'parent_key' });
      } else if (seen.has(key)) {
        issues.push({ clientId: node.clientId, message: 'key_duplicate' });
      } else {
        seen.add(key);
      }
      visit(node.children);
    }
  }

  visit(nodes);
  return issues;
}

export function willReuseExistingType(
  node: StageTreeDraftNode,
  existingKeys: ReadonlySet<string>,
): boolean {
  return existingKeys.has(node.key.trim()) && node.children.length === 0;
}

export function reusedExistingType(
  node: StageTreeDraftNode,
  stageOptions: readonly TaskTypeStageOption[],
  parentKey: string,
): TaskTypeStageOption | undefined {
  if (node.children.length > 0) return undefined;
  const key = node.key.trim();
  if (!key || key === parentKey) return undefined;
  return stageOptions.find((option) => option.key === key);
}

export function mergeAppliedStages(
  current: TaskTypeEditorState,
  saved: TaskTypeResponse,
): TaskTypeEditorState {
  const fromSaved = editorStateFor(saved);
  return {
    ...current,
    expectedSubtasks: fromSaved.expectedSubtasks,
    editing: current.editing
      ? {
          ...current.editing,
          expectedSubtasks: saved.expectedSubtasks,
          expectedSubtaskTypeKeys: saved.expectedSubtaskTypeKeys,
        }
      : current.editing,
  };
}

export function stageOptionsFromTaskTypes(
  taskTypes: readonly TaskTypeResponse[],
): TaskTypeStageOption[] {
  return taskTypes.map((item) => ({
    id: item.id,
    key: item.key,
    name: item.name,
    expectedSubtasks: item.expectedSubtasks ?? [],
  }));
}

export function updateDraftNode(
  nodes: readonly StageTreeDraftNode[],
  clientId: string,
  patch: Partial<Omit<StageTreeDraftNode, 'clientId'>>,
): StageTreeDraftNode[] {
  return nodes.map((node) => {
    if (node.clientId === clientId) {
      return { ...node, ...patch };
    }
    return {
      ...node,
      children: updateDraftNode(node.children, clientId, patch),
    };
  });
}

export function removeDraftNode(
  nodes: readonly StageTreeDraftNode[],
  clientId: string,
): StageTreeDraftNode[] {
  const next: StageTreeDraftNode[] = [];
  for (const node of nodes) {
    if (node.clientId === clientId) continue;
    next.push({
      ...node,
      children: removeDraftNode(node.children, clientId),
    });
  }
  return next;
}

export function moveDraftNode(
  nodes: readonly StageTreeDraftNode[],
  clientId: string,
  direction: -1 | 1,
): StageTreeDraftNode[] {
  const index = nodes.findIndex((node) => node.clientId === clientId);
  if (index >= 0) {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= nodes.length) return [...nodes];
    const next = [...nodes];
    const current = next[index];
    const swap = next[nextIndex];
    if (!current || !swap) return next;
    next[index] = swap;
    next[nextIndex] = current;
    return next;
  }
  return nodes.map((node) => ({
    ...node,
    children: moveDraftNode(node.children, clientId, direction),
  }));
}

export function addChildDraftNode(
  nodes: readonly StageTreeDraftNode[],
  parentClientId: string,
  child: StageTreeDraftNode,
): StageTreeDraftNode[] {
  return nodes.map((node) => {
    if (node.clientId === parentClientId) {
      return { ...node, children: [...node.children, child] };
    }
    return {
      ...node,
      children: addChildDraftNode(node.children, parentClientId, child),
    };
  });
}
